import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { ARTIFACT_NAMES } from '../copy';
import ArtifactCard from './ArtifactCard';

// A result's full card over the conversation, opened from its tile: a panel
// from the right on larger screens, a bottom sheet on phones. Escape, the
// close button or the backdrop close it; focus goes to the close button and
// back to the tile afterwards. The conversation behind doesn't move.
export default function ArtifactDrawer({ artifact, coachName, onAsk, onClose, returnFocus }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!artifact) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
      returnFocus?.focus?.();
    };
  }, [artifact]);

  if (!artifact) return null;
  const name = ARTIFACT_NAMES[artifact.kind] || artifact.title;
  // Asking about it goes back to the conversation, with the composer filled.
  const ask = onAsk ? (a) => { onClose(); onAsk(a); } : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end" data-testid="artifact-drawer">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 w-full h-full border-0 bg-black/40 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={name}
        className="relative flex flex-col w-full sm:w-[min(560px,92vw)] max-h-[88vh] sm:max-h-none sm:h-full bg-background rounded-t-2xl sm:rounded-none sm:border-l border-border shadow-[0_-8px_24px_rgb(0_0_0/0.12)] sm:shadow-[-8px_0_24px_rgb(0_0_0/0.12)]"
      >
        <div className="flex items-center justify-between py-3.5 px-5 border-b border-border">
          <span className="text-[15px] font-semibold text-foreground">{name}</span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 -mr-2 rounded-md border-0 bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-center cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
          <ArtifactCard artifact={artifact} coachName={coachName} onAsk={ask} compact />
        </div>
      </div>
    </div>
  );
}
